import { useState } from 'react'
import { createFileRoute, redirect, useNavigate, Link } from '@tanstack/react-router'
import { KeyRound, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useConfirmPasswordReset } from '@/features/auth/hooks'
import wavesLogo from '@/assets/waves.svg'

export const Route = createFileRoute('/reset-password')({
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  beforeLoad: ({ search }) => {
    if (!search.token) throw redirect({ to: '/login' })
  },
  component: ResetPasswordPage,
})

function ResetPasswordPage() {
  const navigate = useNavigate()
  const confirm = useConfirmPasswordReset()
  const token = Route.useSearch().token ?? ''
  const [next, setNext] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (next.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (next !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    confirm.mutate(
      { token, next },
      {
        onSuccess: () => navigate({ to: '/' }),
        onError: (err) => setError(err.message || 'This reset link is invalid or has expired.'),
      },
    )
  }

  return (
    <div className="bg-background flex min-h-svh items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="items-center text-center">
          <img src={wavesLogo} alt="FlowPilot logo" className="mx-auto mb-2 h-14 w-14" />
          <CardTitle className="text-xl">Choose a new password</CardTitle>
          <CardDescription>
            Your reset link is valid for 30 minutes and works once. Setting a new password signs out
            all devices.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="grid gap-3">
            <div className="grid gap-2">
              <Label htmlFor="new-password">New password</Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                placeholder="min 8 characters"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                disabled={confirm.isPending}
                required
                autoFocus
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="confirm-password">Confirm password</Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={confirm.isPending}
                required
              />
            </div>
            {error && <p className="text-destructive text-sm">{error}</p>}
            <Button type="submit" disabled={confirm.isPending}>
              {confirm.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <KeyRound className="mr-2 h-4 w-4" />
              )}
              Reset password & sign in
            </Button>
            <Link
              to="/login"
              className="text-muted-foreground hover:text-foreground justify-self-center text-sm underline-offset-4 hover:underline"
            >
              Back to sign in
            </Link>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
